import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Unlink, Search } from 'lucide-react';
import { dateLocale } from '../../lib/dateFormat';
import { DateText } from '../ui/week-badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssetExpenses = ({ asset, isAdmin, onRefresh, navigateTo, onClose }) => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkOpen, setLinkOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchExpenses = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/inventory/assets/${asset.id}/expenses`); setExpenses(r.data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchExpenses(); }, [asset.id]);

  const handleSearch = async () => {
    setSearching(true);
    try { const r = await axios.get(`${API}/inventory/expenses/search`, { params: { q: searchQ } }); setSearchResults(r.data); }
    catch { toast.error(t('inv.expenses.searchFailed')); }
    setSearching(false);
  };

  useEffect(() => {
    if (linkOpen) handleSearch();
  }, [linkOpen]);

  const handleLink = async (expenseId) => {
    try {
      await axios.post(`${API}/inventory/assets/${asset.id}/link-expense/${expenseId}`);
      toast.success(t('inv.expenses.linked'));
      fetchExpenses();
      onRefresh();
      setSearchResults(prev => prev.filter(e => e.id !== expenseId));
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.expenses.linkFailed')); }
  };

  const handleUnlink = async (eid) => {
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/unlink-expense/${eid}`);
      toast.success(t('inv.expenses.unlinked'));
      fetchExpenses();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.expenses.unlinkFailed')); }
  };

  const linkedIds = new Set(
    (asset.linked_expense_ids || []).concat(expenses.map(e => e.id))
  );

  const isExpenseLinked = (expense) => {
    if (linkedIds.has(expense.id)) return true;
    if (expense.asset_id && expense.asset_id === asset.id) return true;
    return false;
  };
  const totalCost = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="space-y-3" data-testid="asset-expenses">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h4 className="text-sm font-semibold">{t('inv.expenses.title', { count: expenses.length })}</h4>
          {expenses.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('inv.expenses.totalRunning', { total: totalCost.toLocaleString() })}
            </p>
          )}
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setLinkOpen(true)} data-testid="link-expense-btn">
            <Plus className="w-4 h-4 mr-1" /> {t('inv.expenses.linkExpense')}
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('common.loading')}</p>
      ) : expenses.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('inv.expenses.empty')}</p>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t('inv.common.date')}</TableHead>
            <TableHead>{t('inv.common.description')}</TableHead>
            <TableHead>{t('inv.common.category')}</TableHead>
            <TableHead className="text-right">{t('inv.common.amount')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            {isAdmin && <TableHead className="w-[40px]" />}
          </TableRow></TableHeader>
          <TableBody>
            {expenses.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="text-sm"><DateText value={e.expense_date} empty="-" /></TableCell>
                <TableCell className="text-sm">
                  {navigateTo ? (
                    <button className="text-blue-600 hover:underline text-sm"
                      onClick={() => { onClose?.(); navigateTo('budget', { filterAssetId: asset.id, filterAssetName: asset.name }); }}
                      data-testid={`nav-expense-${e.id}`}>{e.description || t('inv.expenses.viewInBudget')}</button>
                  ) : (e.description || '-')}
                </TableCell>
                <TableCell className="text-sm">{e.category || '-'}</TableCell>
                <TableCell className="text-right font-mono text-sm">{e.amount?.toLocaleString()} {e.currency || ''}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{e.status || t('inv.common.unknown')}</Badge></TableCell>
                {isAdmin && (
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-500"
                      onClick={() => handleUnlink(e.id)} title={t('inv.expenses.unlink')}><Unlink className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg max-h-[80vh]" data-testid="link-expense-dialog">
          <DialogHeader>
            <DialogTitle>{t('inv.expenses.linkExpense')}</DialogTitle>
            <DialogDescription className="sr-only">{t('inv.expenses.searchHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={t('inv.expenses.searchPlaceholder')} className="pl-9" data-testid="expense-search-input" />
              </div>
              <Button variant="outline" size="sm" onClick={handleSearch} disabled={searching} data-testid="btn-search">
                {searching ? '…' : t('common.search')}
              </Button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto space-y-1">
              {searchResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('inv.expenses.noneFound')}</p>
              ) : (
                searchResults.map((e) => {
                  const alreadyLinked = isExpenseLinked(e);
                  return (
                    <div key={e.id} className={`flex items-center justify-between py-2 px-3 rounded border ${alreadyLinked ? 'bg-muted opacity-60' : 'hover:bg-muted/50'}`} data-testid={`expense-option-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{e.description || t('inv.expenses.noDescription')}</p>
                        <p className="text-xs text-muted-foreground">{e.date} | {e.category || '—'} | {e.amount?.toLocaleString()} {e.currency}</p>
                      </div>
                      <Button size="sm" variant={alreadyLinked ? 'ghost' : 'default'} disabled={alreadyLinked}
                        onClick={() => handleLink(e.id)} className="ml-2 shrink-0" data-testid={`link-expense-option-${e.id}`}>
                        {alreadyLinked ? t('inv.expenses.alreadyLinked') : t('inv.expenses.link')}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetExpenses;
